import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/CartContext';

// Load Razorpay script dynamically
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });
};

const CheckoutPage = () => {
  const { items, cartTotal, clearCart } = useCart();
  const toast = useToast();
  const navigate = useNavigate();
  const [apartments, setApartments] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ customerName: '', phone: '', address: '', apartmentId: '', notes: '' });

  // Payment failure modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [failedOrderId, setFailedOrderId] = useState(null);
  const [failedOrderAmount, setFailedOrderAmount] = useState(null);
  const [modalAction, setModalAction] = useState(null); // 'retrying' | 'switching' | 'cancelling'

  useEffect(() => {
    if (items.length === 0 && !showPaymentModal) navigate('/cart');
    api.get('/apartments').then(({ data }) => setApartments(data.data)).catch(() => { });
    // Preload Razorpay script
    loadRazorpayScript();
  }, [items, navigate, showPaymentModal]);

  const tax = cartTotal * 0.05;
  const total = cartTotal + tax;

  const openRazorpay = useCallback(async (orderId, totalAmount) => {
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      toast('Failed to load payment gateway. Please try again.', 'error');
      setLoading(false);
      return;
    }

    try {
      // Create Razorpay order via backend
      const { data: paymentData } = await api.post('/payments/create-order', {
        orderId,
        amount: totalAmount,
      });

      if (!paymentData.success) {
        toast('Failed to initiate payment. Please try again.', 'error');
        setLoading(false);
        return;
      }

      const { razorpayOrderId, keyId } = paymentData.data;

      // Open Razorpay Checkout
      const options = {
        key: keyId,
        amount: Math.round(totalAmount * 100),
        currency: 'INR',
        name: 'V2V SUPERFRESH',
        description: `Order #${orderId.slice(0, 8).toUpperCase()}`,
        order_id: razorpayOrderId,
        handler: async (response) => {
          // Payment successful — verify on backend
          try {
            const { data: verifyData } = await api.post('/payments/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              orderId,
            });

            if (verifyData.success) {
              clearCart();
              navigate('/confirmation', {
                state: {
                  orderId,
                  totalAmount,
                  paymentId: response.razorpay_payment_id,
                  paymentMethod: 'online',
                },
              });
            } else {
              toast('Payment verification failed. Contact support.', 'error');
              // Show modal to retry or switch
              setFailedOrderId(orderId);
              setFailedOrderAmount(totalAmount);
              setShowPaymentModal(true);
            }
          } catch (err) {
            console.error('Verify error:', err);
            toast('Payment verification failed. Your payment may have been received — contact support if charged.', 'error');
            setFailedOrderId(orderId);
            setFailedOrderAmount(totalAmount);
            setShowPaymentModal(true);
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: form.customerName,
          contact: form.phone.replace('+91 ', '').replace(/\s/g, ''),
        },
        notes: {
          orderId,
          address: form.address,
        },
        theme: {
          color: '#059669',
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            // User cancelled — show modal with options
            setFailedOrderId(orderId);
            setFailedOrderAmount(totalAmount);
            setShowPaymentModal(true);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        console.error('Payment failed:', response.error);
        toast(`Payment failed: ${response.error.description}`, 'error');
        setLoading(false);
        // Show modal to retry or switch
        setFailedOrderId(orderId);
        setFailedOrderAmount(totalAmount);
        setShowPaymentModal(true);
      });
      rzp.open();
    } catch (err) {
      console.error('Razorpay error:', err);
      toast('Failed to initiate payment. Please try again.', 'error');
      setLoading(false);
    }
  }, [form, clearCart, navigate, toast]);

  // Retry payment for the same order
  const handleRetryPayment = async () => {
    if (!failedOrderId || !failedOrderAmount) return;
    setModalAction('retrying');
    setShowPaymentModal(false);
    setLoading(true);
    await openRazorpay(failedOrderId, failedOrderAmount);
    setModalAction(null);
  };

  // Switch to COD
  const handleSwitchToCod = async () => {
    if (!failedOrderId) return;
    setModalAction('switching');
    try {
      const { data } = await api.post(`/orders/${failedOrderId}/switch-to-cod`);
      if (data.success) {
        clearCart();
        toast('Order switched to Cash on Delivery!', 'success');
        navigate('/confirmation', {
          state: {
            orderId: failedOrderId,
            totalAmount: failedOrderAmount,
            paymentMethod: 'cod',
          },
        });
      } else {
        toast('Failed to switch to COD. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Switch to COD error:', err);
      toast(err.response?.data?.message || 'Failed to switch to COD.', 'error');
    } finally {
      setModalAction(null);
      setShowPaymentModal(false);
      setLoading(false);
    }
  };

  // Cancel the unpaid order entirely
  const handleCancelOrder = async () => {
    if (!failedOrderId) return;
    setModalAction('cancelling');
    try {
      await api.delete(`/orders/${failedOrderId}/cancel-unpaid`);
      toast('Order cancelled.', 'info');
      setShowPaymentModal(false);
      setFailedOrderId(null);
      setFailedOrderAmount(null);
    } catch (err) {
      console.error('Cancel order error:', err);
      toast(err.response?.data?.message || 'Failed to cancel order.', 'error');
    } finally {
      setModalAction(null);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customerName || !form.phone || !form.address) {
      toast('Please fill all required fields', 'error');
      return;
    }
    setLoading(true);
    try {
      const orderItems = items.map(i => ({ productId: i.id, quantity: i.quantity }));
      const { data } = await api.post('/orders', {
        ...form,
        apartmentId: form.apartmentId || null,
        items: orderItems,
        paymentMethod,
      });

      const orderId = data.data.orderId;
      const totalAmount = data.data.totalAmount;

      if (paymentMethod === 'online') {
        // Opens Razorpay — order is in 'awaiting_payment' status
        // Stock is NOT deducted yet — only deducted after payment verification
        await openRazorpay(orderId, totalAmount);
      } else {
        // COD — order is finalized immediately
        clearCart();
        navigate('/confirmation', {
          state: { orderId, totalAmount, paymentMethod: 'cod' },
        });
      }
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to place order. Please try again.', 'error');
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <h1 style={{ fontSize: 26, fontWeight: 900, marginBottom: 28 }}>🏠 Checkout</h1>
      <form onSubmit={handleSubmit}>
        <div className="checkout-layout">
          {/* Left - delivery info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Delivery details */}
            <div className="checkout-card">
              <h2>📦 Delivery Details</h2>
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-input" id="checkout-name" placeholder="Your full name" value={form.customerName} onChange={e => setForm(p => ({ ...p, customerName: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number *</label>
                <div style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                  <div style={{ padding: '0 16px', fontWeight: 600, color: 'var(--text-3)', background: '#f8fafc', borderRight: '1px solid var(--border)', height: '100%', display: 'flex', alignItems: 'center' }}>+91</div>
                  <input className="form-input" id="checkout-phone" type="tel" placeholder="9876543210" style={{ border: 'none', borderRadius: 0 }} value={form.phone.replace('+91 ', '')} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 10); setForm(p => ({ ...p, phone: v ? `+91 ${v}` : '' })) }} required pattern="[0-9]{10}" maxLength="10" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Apartment *</label>
                <select className="form-select" id="checkout-apartment" value={form.apartmentId} onChange={e => setForm(p => ({ ...p, apartmentId: e.target.value }))}>
                  <option value="">Select your apartment building...</option>
                  {apartments.map(a => <option key={a.id} value={a.id}>{a.apartment_name} {a.location ? `— ${a.location}` : ''}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Flat/Door Number & Address *</label>
                <textarea className="form-textarea" id="checkout-address" placeholder="e.g. Flat 4B, Tower 2" value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Delivery Notes (optional)</label>
                <input className="form-input" placeholder="Leave at door, call before delivery, etc." value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>

            {/* Payment */}
            <div className="checkout-card">
              <h2>💳 Payment Method</h2>
              <div className="payment-options">
                <div className={`payment-option ${paymentMethod === 'cod' ? 'selected' : ''}`} onClick={() => setPaymentMethod('cod')}>
                  <div className="pay-icon">💵</div>
                  <div className="pay-label">Cash on Delivery</div>
                  <div className="pay-sub">Pay when delivered</div>
                </div>
                <div className={`payment-option ${paymentMethod === 'online' ? 'selected' : ''}`} onClick={() => setPaymentMethod('online')}>
                  <div className="pay-icon">💳</div>
                  <div className="pay-label">Pay Online</div>
                  <div className="pay-sub">UPI / Card / Wallet</div>
                </div>
              </div>
              {paymentMethod === 'online' && (
                <div style={{ marginTop: 14, padding: 12, background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0', fontSize: 13, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18 }}>🔒</span>
                  <span>Secure payment via <strong>Razorpay</strong> — UPI, Credit/Debit Card, Net Banking, Wallets supported.</span>
                </div>
              )}
            </div>
          </div>

          {/* Right - summary */}
          <div>
            <div className="cart-summary">
              <div className="summary-title">🛒 Order Summary</div>
              {items.map(item => (
                <div key={item.id} className="summary-row">
                  <span>{item.name} × {item.quantity}</span>
                  <span>₹{(parseFloat(item.price) * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className="summary-row"><span>Subtotal</span><span>₹{cartTotal.toFixed(2)}</span></div>
              <div className="summary-row"><span>GST (5%)</span><span>₹{tax.toFixed(2)}</span></div>
              <div className="summary-row"><span>🚚 Delivery</span><span style={{ color: 'var(--green)', fontWeight: 600 }}>FREE</span></div>
              <div className="summary-total">
                <span className="label">Total</span>
                <span className="value">₹{total.toFixed(2)}</span>
              </div>
              <button
                id="place-order-btn"
                type="submit"
                className="btn btn-green btn-full"
                style={{ marginTop: 20, padding: '15px', fontSize: 16 }}
                disabled={loading}
              >
                {loading
                  ? <><span className="spinner-sm"></span> {paymentMethod === 'online' ? 'Processing Payment...' : 'Placing Order...'}</>
                  : paymentMethod === 'online'
                    ? `🔒 Pay & Order — ₹${total.toFixed(2)}`
                    : `✅ Place Order — ₹${total.toFixed(2)}`
                }
              </button>
              <div style={{ textAlign: 'center', marginTop: 12, fontSize: 12, color: 'var(--text-3)' }}>
                {paymentMethod === 'online'
                  ? '🔐 Secured by Razorpay · 256-bit encryption'
                  : 'No account needed · Free cancellation'
                }
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* ── Payment Failed / Cancelled Modal ─────────────────────────────── */}
      {showPaymentModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: 20, backdropFilter: 'blur(4px)',
        }}>
          <div style={{
            background: '#fff', borderRadius: 20, maxWidth: 440, width: '100%',
            padding: '32px 28px', boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
            animation: 'fadeInUp 0.3s ease',
          }}>
            {/* Icon */}
            <div style={{ textAlign: 'center', fontSize: 56, marginBottom: 8 }}>⚠️</div>
            
            <h2 style={{ textAlign: 'center', fontSize: 22, fontWeight: 800, margin: '0 0 8px', color: '#1e293b' }}>
              Payment Not Completed
            </h2>
            <p style={{ textAlign: 'center', fontSize: 14, color: '#64748b', margin: '0 0 24px', lineHeight: 1.5 }}>
              Your order <strong>#{failedOrderId?.slice(0, 8).toUpperCase()}</strong> is not confirmed yet. 
              Choose how you'd like to proceed:
            </p>

            {/* Amount display */}
            <div style={{
              background: '#f8fafc', borderRadius: 12, padding: '12px 16px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 20, border: '1px solid #e2e8f0',
            }}>
              <span style={{ fontSize: 13, color: '#64748b' }}>Order Amount</span>
              <span style={{ fontSize: 20, fontWeight: 800, color: '#059669' }}>₹{parseFloat(failedOrderAmount || 0).toFixed(2)}</span>
            </div>

            {/* Option 1: Retry Payment */}
            <button
              onClick={handleRetryPayment}
              disabled={!!modalAction}
              style={{
                width: '100%', padding: '14px 20px', borderRadius: 12,
                background: 'linear-gradient(135deg, #059669, #047857)', color: '#fff',
                border: 'none', fontSize: 15, fontWeight: 700, cursor: modalAction ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                marginBottom: 10, opacity: modalAction && modalAction !== 'retrying' ? 0.5 : 1,
                transition: 'all 0.2s',
              }}
            >
              {modalAction === 'retrying' ? <><span className="spinner-sm"></span> Opening Payment...</> : '🔒 Retry Payment'}
            </button>

            {/* Option 2: Switch to COD */}
            <button
              onClick={handleSwitchToCod}
              disabled={!!modalAction}
              style={{
                width: '100%', padding: '14px 20px', borderRadius: 12,
                background: '#f0f9ff', color: '#0369a1',
                border: '2px solid #bae6fd', fontSize: 15, fontWeight: 700, cursor: modalAction ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                marginBottom: 10, opacity: modalAction && modalAction !== 'switching' ? 0.5 : 1,
                transition: 'all 0.2s',
              }}
            >
              {modalAction === 'switching' ? <><span className="spinner-sm"></span> Switching...</> : '💵 Switch to Cash on Delivery'}
            </button>

            {/* Option 3: Cancel */}
            <button
              onClick={handleCancelOrder}
              disabled={!!modalAction}
              style={{
                width: '100%', padding: '12px 20px', borderRadius: 12,
                background: 'transparent', color: '#ef4444',
                border: '1px solid #fecaca', fontSize: 14, fontWeight: 600, cursor: modalAction ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                opacity: modalAction && modalAction !== 'cancelling' ? 0.5 : 1,
                transition: 'all 0.2s',
              }}
            >
              {modalAction === 'cancelling' ? <><span className="spinner-sm"></span> Cancelling...</> : '✕ Cancel Order'}
            </button>

            <p style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', marginTop: 16, marginBottom: 0 }}>
              No payment has been charged. Your cart items are safe.
            </p>
          </div>
        </div>
      )}

      {/* Inline keyframes for modal animation */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default CheckoutPage;
